/**
 * Tenant-owned people and provider-alias contracts.
 *
 * Provider credentials and SDK-specific account objects deliberately do not
 * cross this boundary. An alias is only confirmed when it is inside Person.
 */
export const PERSON_PROVIDERS = ['slack', 'jira', 'github'];
export const PEOPLE_ERROR_CODES = {
    invalidArgument: 'PERSON_INVALID_ARGUMENT',
    notFound: 'PERSON_NOT_FOUND',
    conflict: 'PERSON_REVISION_CONFLICT',
    aliasClaimed: 'PERSON_ALIAS_CLAIMED',
    aliasNotFound: 'PERSON_ALIAS_NOT_FOUND',
    suggestionNotFound: 'PERSON_SUGGESTION_NOT_FOUND',
    providerUnavailable: 'PERSON_PROVIDER_UNAVAILABLE',
};
export function isPersonProvider(value) {
    return typeof value === 'string' && PERSON_PROVIDERS.includes(value);
}
export function isPersonMentionToken(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const token = value;
    return (token.type === 'person-mention' &&
        typeof token.personId === 'string' &&
        token.personId.trim().length > 0 &&
        typeof token.aliasId === 'string' &&
        token.aliasId.trim().length > 0 &&
        isPersonProvider(token.platform) &&
        typeof token.label === 'string' &&
        token.label.trim().length > 0);
}
export function aliasClaimKey(platform, externalId) {
    return `${platform}__${encodeURIComponent(externalId.trim())}`;
}
export function preferredAliasFor(person, platform) {
    const preferredId = person.preferredAliasIds?.[platform];
    return (person.aliases.find((alias) => alias.platform === platform && alias.id === preferredId) ??
        person.aliases.find((alias) => alias.platform === platform));
}
/** Associate received provider records only through confirmed stable IDs. */
export function personIdForProviderAlias(people, platform, externalId) {
    const normalized = externalId.trim();
    if (!normalized)
        return undefined;
    return people.find((person) => person.aliases.some((alias) => alias.platform === platform && alias.externalId === normalized))?.id;
}
export function slackMentionMarkup(alias) {
    assertAliasPlatform(alias, 'slack');
    return `<@${alias.externalId}>`;
}
/** Resolve editor-only Slack mention links against a local people snapshot. */
export function resolveSlackPersonMentionsFromPeople(markdown, people) {
    const mentionPattern = /\[([^\]]+)\]\(person-mention:([^:]+):([^:]+):([^\s)]+)\)/gi;
    const matches = [...markdown.matchAll(mentionPattern)];
    const personLinkCount = (markdown.match(/\]\([^)]*person-mention:/gi) ?? []).length;
    if (personLinkCount !== matches.length) {
        throw personMentionError('The person mention link is malformed.');
    }
    let output = '';
    let cursor = 0;
    for (const match of matches) {
        const [, , rawPlatform, rawPersonId, rawAliasId] = match;
        if (rawPlatform.toLowerCase() !== 'slack') {
            throw personMentionError(`A Slack reply cannot contain a ${rawPlatform} mention.`);
        }
        const personId = decodeMentionPart(rawPersonId);
        const aliasId = decodeMentionPart(rawAliasId);
        const person = people.find((candidate) => candidate.id === personId);
        const alias = person?.aliases.find((candidate) => candidate.id === aliasId && candidate.platform === 'slack');
        if (!alias) {
            throw personMentionError('The mention alias is stale or no longer confirmed.', 409);
        }
        output += markdown.slice(cursor, match.index);
        output += slackMentionMarkup(alias);
        cursor = (match.index ?? 0) + match[0].length;
    }
    return output + markdown.slice(cursor);
}
function decodeMentionPart(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        throw personMentionError('The person mention link is malformed.');
    }
}
function personMentionError(message, status = 400) {
    return Object.assign(new Error(message), {
        status,
        code: status === 409 ? PEOPLE_ERROR_CODES.aliasNotFound : PEOPLE_ERROR_CODES.invalidArgument,
    });
}
export function jiraMentionNode(alias) {
    assertAliasPlatform(alias, 'jira');
    return { type: 'mention', attrs: { id: alias.externalId, text: alias.displayName } };
}
export function githubMentionMarkdown(alias, currentLogin) {
    assertAliasPlatform(alias, 'github');
    const login = (currentLogin ?? alias.username ?? '').trim();
    if (!/^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})$/.test(login)) {
        throw new Error('A current GitHub login is required to render a mention.');
    }
    return `@${login}`;
}
function assertAliasPlatform(alias, expected) {
    if (alias.platform !== expected || !alias.externalId.trim()) {
        throw new Error(`Alias is not a valid ${expected} alias.`);
    }
}
//# sourceMappingURL=people.js.map